import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { cloneElement, ReactElement, ReactNode, useEffect, useState } from 'react'

import { SVGFC, SVGProps } from '../../../../toolkit/src/types/svg.types'
import { ExclamationMarkIcon } from '../../../icons'
import Typography from '../typography'

type ChildProps = {
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  className?: string
}

export interface InputWrapperProps {
  /*
   * The label that will be displayed inside and above the wrapper
   */
  label?: string

  /*
   * If true, we display a '*' after the label
   */
  required?: boolean

  /*
   * If true, the wrapper will be highlighted
   */
  highlighted?: boolean

  /*
   * The error message that will be displayed below the wrapper
   */
  errorMessage?: string

  /**
   *  If true, the error message will be displayed in a blank state
   */
  hasBlankError?: boolean

  /*
   * The icon that will be displayed on the left side of the children
   */
  icon?: SVGFC

  /*
   * The style for the icon
   */
  iconStyle?: SVGProps

  /*
   * Click handler for the icon (if provided the icon will be clickable and
   * the click will not propagate to the wrapper)
   */
  iconOnClick?: () => void

  /*
   * The suffix that will be displayed on the right side of the children
   */
  suffix?: string | ReactNode

  /*
   * If active is true, the label will be in the active state
   */
  active?: boolean

  /*
   * If focused is true, the label will be in the focused state
   */
  focused?: boolean

  /*
   * The children should be an input element for best results, but it can be any element
   */
  children:
    | ReactElement
    | ((isActive: boolean, setIsActive: (value: boolean) => void) => ReactElement)

  /*
   * The class name for the wrapper
   */
  className?: string

  /*
   * The wrapper class name for the wrapper
   */
  wrapperClassName?: string

  /*
   * If true, the icon will be displayed to the right of the input
   */
  iconToTheRight?: boolean

  /*
   * If true, the input will be disabled
   */
  disabled?: boolean

  /*
   * Read only state of the input
   */
  readOnly?: boolean

  /*
   * The label class name
   */
  labelClassName?: string

  /*
   * The container class name
   */
  containerClassName?: string

  /*
   * The onClick function for the wrapper
   */
  onClick?: () => void

  /*
   * The onBlur function for the input
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

const InputWrapper: React.FC<InputWrapperProps> = ({
  label,
  required,
  icon,
  iconStyle,
  suffix,
  errorMessage,
  focused,
  active,
  children,
  className,
  wrapperClassName,
  iconToTheRight,
  disabled,
  labelClassName,
  containerClassName,
  readOnly,
  hasBlankError,
  highlighted,
  onClick,
  iconOnClick
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isActive, setIsActive] = useState(!!active)

  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement>,
    originalFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  ) => {
    setIsFocused(true)
    if (originalFocus) originalFocus(e)
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    originalBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  ) => {
    setIsFocused(false)
    if (originalBlur) originalBlur(e)
  }

  const child = typeof children === 'function' ? children(isActive, setIsActive) : children

  const childWithProps = cloneElement(child, {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
      handleFocus(e, (child.props as ChildProps).onFocus),
    onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
      handleBlur(e, (child.props as ChildProps).onBlur),
    className: cn('py-2.5 w-full h-full', (child.props as ChildProps).className)
  })

  useEffect(() => {
    setIsActive(!!active)
  }, [active])

  useEffect(() => {
    setIsFocused(!!focused)
  }, [focused])

  return (
    <div className={containerClassName}>
      <div
        onClick={onClick}
        className={cn(
          'relative group items-center w-full border justify-between border-grey-stroke rounded focus-within:border-primary min-h-[37px]',
          {
            'focus-within:border-red border-red': errorMessage || hasBlankError,
            'border-grey-light-active focus-within:border-grey-light-active': disabled,
            'pointer-events-none': disabled || readOnly,
            'border-primary bg-primary-light': highlighted
          },
          wrapperClassName
        )}
      >
        <div
          className={cn(
            'flex items-center gap-1 px-4 min-h-[38px]',
            { 'px-3': icon, 'flex-row-reverse': iconToTheRight || suffix },
            className
          )}
        >
          {iconOnClick && !disabled && !readOnly ? (
            <div
              onClick={(e) => {
                e.stopPropagation()
                iconOnClick()
              }}
              className="flex items-center cursor-pointer"
            >
              {icon?.({
                color: disabled
                  ? tailwindColors.grey.light.hover
                  : isFocused
                    ? tailwindColors.primary.DEFAULT
                    : tailwindColors.grey.lighter,
                width: '20px',
                height: '20px',
                ...iconStyle
              })}
            </div>
          ) : (
            icon?.({
              color: disabled
                ? tailwindColors.grey.light.hover
                : isFocused
                  ? tailwindColors.primary.DEFAULT
                  : tailwindColors.grey.lighter,
              width: '20px',
              height: '20px',
              ...iconStyle
            })
          )}
          {suffix &&
            (typeof suffix === 'string' ? (
              <Typography variant="label-medium">{suffix}</Typography>
            ) : (
              suffix
            ))}

          <div className="relative w-full h-full">
            {label && (
              <label
                className={cn(
                  'absolute bg-white pointer-events-none left-0 transition-all duration-200 flex items-center origin-center gap-1',
                  {
                    '-top-[8px] px-1 -left-2': isFocused || isActive,
                    'top-[18px] transform -translate-y-1/2 w-3/4': !isFocused && !isActive,
                    'text-grey': !errorMessage,
                    'text-red': errorMessage || hasBlankError,
                    '-top-[10px]': errorMessage && (isFocused || isActive),
                    'bg-primary-light': highlighted
                  },
                  labelClassName
                )}
              >
                <Typography
                  style={{
                    fontSize: isFocused || isActive ? '9px' : '12px'
                  }}
                  variant="label"
                  color={
                    disabled
                      ? tailwindColors.grey.light.active
                      : errorMessage || hasBlankError
                        ? tailwindColors.red.DEFAULT
                        : highlighted
                          ? tailwindColors.primary.DEFAULT
                          : tailwindColors.grey.DEFAULT
                  }
                >
                  {StringFormatter.isRequired(label, required)}
                </Typography>
                {errorMessage && (
                  <ExclamationMarkIcon
                    color={tailwindColors.white.DEFAULT}
                    fill={tailwindColors.red.DEFAULT}
                    width="12px"
                    height="12px"
                  />
                )}
              </label>
            )}

            {childWithProps}
          </div>
        </div>
      </div>
      {errorMessage && (
        <div>
          <div className="h-1" />
          <Typography variant="small" color={tailwindColors.red.DEFAULT}>
            {errorMessage}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default InputWrapper
